import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatBlockDate } from "../components/ApplicationAccess/blockDates";
import { branchFeatureFlags } from "../config/branchFeatureFlags";
import { _axios } from "../utils/axios";

/** What a block is placed on: one user, or every user of a user type. */
export type TAccessScope = "user" | "user_type";

/** A block that applies today. `until` is its last day (YYYY-MM-DD); null means until it is lifted. */
export type TAccessBlock = {
  scope: TAccessScope;
  until: string | null;
  reason: string | null;
  blockedBy: string;
};

export type TMyApplicationAccess = {
  blocked: boolean;
  block: TAccessBlock | null;
};

export type TAccessUserType = {
  userType: string;
  block: TAccessBlock | null;
};

export type TAccessUser = {
  userId: number;
  name: string;
  mobileNumber: string;
  userType: string;
  block: TAccessBlock | null;
};

export type TAccessUsersPage = {
  users: TAccessUser[];
  total: number;
  page: number;
  pageSize: number;
};

export type TAccessTarget = { scope: TAccessScope; target: string };

const BASE = "/application-access";
const KEY = "application-access";

/** The logged-in applicant's own access; the screens ask this and nothing else. */
export function useMyApplicationAccess() {
  return useQuery({
    queryKey: [KEY, "me"],
    queryFn: () =>
      _axios.get<{ data: TMyApplicationAccess }>(`${BASE}/me`),
    select: (d) => d?.data?.data,
    enabled: branchFeatureFlags.ENABLE_APPLICATION_ACCESS_CONTROL,
    staleTime: 0,
  });
}

/**
 * The one thing an applicant screen needs to know: is this applicant blocked,
 * and the sentence to show. A button that starts or submits an application
 * switches itself off with `isBlocked` and uses `notice` as its tooltip.
 */
export function useApplicationAccessBlock() {
  const { data } = useMyApplicationAccess();
  const block = data?.blocked ? data.block : null;
  if (!block) return { isBlocked: false, notice: "" };

  const until = block.until ? ` until ${formatBlockDate(block.until)}` : "";
  const reason = block.reason ? ` Reason: ${block.reason}` : "";
  return {
    isBlocked: true,
    notice: `Your account is restricted from creating or submitting applications${until}.${reason}`,
  };
}

export function useAccessUserTypes() {
  return useQuery({
    queryKey: [KEY, "user-types"],
    queryFn: () =>
      _axios.get<{ data: TAccessUserType[] }>(`${BASE}/user-types`),
    select: (d) => d?.data?.data ?? [],
  });
}

export function useAccessUsers(params: {
  search: string;
  userType: string;
  page: number;
}) {
  return useQuery({
    queryKey: [KEY, "users", params],
    queryFn: () =>
      _axios.get<{ data: TAccessUsersPage }>(`${BASE}/users`, { params }),
    select: (d) => d?.data?.data,
  });
}

/** Places a block. Every Application Access list is read again afterwards. */
export function useBlockApplicationAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: TAccessTarget & { until: string | null; reason: string }
    ) => _axios.post(`${BASE}/block`, body),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Lifts a block. Every Application Access list is read again afterwards. */
export function useUnblockApplicationAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TAccessTarget) => _axios.post(`${BASE}/unblock`, body),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
